import React, { useEffect, useMemo, useState } from "react";
import api from "../auth/api"; // ajusta la ruta si tu api está en otra carpeta

function cls(...a) { return a.filter(Boolean).join(" "); }

function fmtDate(dateStr) {
  if (!dateStr) return "";
  try {
    // dateStr viene como 2026-03-04T00:00:00.000Z o 2026-03-04...
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr).slice(0, 10);
    return d.toLocaleDateString();
  } catch {
    return String(dateStr).slice(0, 10);
  }
}

function fmtTime(hora) {
  if (!hora) return "";
  // si viene "HH:MM:SS" o "HH:MM:SS.0000000"
  const s = String(hora);
  const m = s.match(/^(\d{2}:\d{2}:\d{2})/);
  return m ? m[1] : s.slice(0, 8);
}

export default function BitacoraHistoryModal({
  open,
  onClose,
  contrato,          // obligatorio para consultar
  usuarioLabel = "", // opcional para mostrar arriba
  pageSizeDefault = 10,
}) {
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [contestar, setContestar] = useState(""); // "" all | "0" | "1"
  const [order, setOrder] = useState("desc"); // asc|desc

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeDefault);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // reset al abrir
  useEffect(() => {
    if (!open) return;
    setErr("");
    setPage(1);
  }, [open]);

  const canQuery = useMemo(() => {
    const c = String(contrato || "").trim();
    return !!c;
  }, [contrato]);

  const fetchHistory = async (opts = {}) => {
    if (!canQuery) return;
    setLoading(true);
    setErr("");

    const p = opts.page ?? page;
    const ps = opts.pageSize ?? pageSize;

    try {
      const { data } = await api.get(`/contratos/${encodeURIComponent(String(contrato).trim())}/novedades`, {
        params: {
          page: p,
          pageSize: ps,
          q: q || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          contestar: contestar === "" ? undefined : contestar,
          order: order || "desc",
        },
      });

      setRows(data?.rows || []);
      setPagination(data?.pagination || null);
    } catch (e) {
      setRows([]);
      setPagination(null);
      setErr(e?.response?.data?.error || e?.message || "Error consultando bitácoras");
    } finally {
      setLoading(false);
    }
  };

  // carga cada vez que cambian filtros (con debounce suave)
  useEffect(() => {
    if (!open) return;
    if (!canQuery) return;

    const t = setTimeout(() => {
      setPage(1);
      fetchHistory({ page: 1 });
    }, 250);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canQuery, q, dateFrom, dateTo, contestar, order, pageSize]);

  // cuando cambia page
  useEffect(() => {
    if (!open) return;
    if (!canQuery) return;
    fetchHistory({ page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const pages = pagination?.pages || 1;
  const total = pagination?.total || 0;

  const pageButtons = useMemo(() => {
    // pequeño paginador: muestra ~7 botones alrededor
    const out = [];
    const max = pages;
    const cur = page;
    const start = Math.max(1, cur - 3);
    const end = Math.min(max, cur + 3);

    if (start > 1) out.push(1);
    if (start > 2) out.push("…");
    for (let i = start; i <= end; i++) out.push(i);
    if (end < max - 1) out.push("…");
    if (end < max) out.push(max);

    return out;
  }, [page, pages]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" role="dialog" aria-modal="true">
      {/* overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" onClick={() => onClose?.()} />

      {/* modal */}
      <div className="relative w-[980px] max-w-[94vw] rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden animate-[modalIn_.14s_ease-out]">
        {/* header */}
        <div className="px-5 py-4 border-b bg-gradient-to-r from-slate-50 to-white flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="font-extrabold text-lg text-slate-900">
              Historial de Bitácoras
            </div>
            <div className="text-sm text-slate-600 mt-0.5 truncate">
              Contrato: <b className="text-slate-900">{String(contrato || "—")}</b>
              {usuarioLabel ? <> · Usuario: <b className="text-slate-900">{usuarioLabel}</b></> : null}
              {pagination ? <> · Total: <b className="text-slate-900">{total}</b></> : null}
            </div>
          </div>

          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              className="px-3 py-1.5 rounded-xl border border-slate-200 text-slate-900 hover:bg-slate-50 active:scale-[.98]"
              onClick={() => fetchHistory({ page })}
              title="Refrescar"
              disabled={loading || !canQuery}
            >
              ⟳
            </button>

            <button
              type="button"
              className="px-3 py-1.5 rounded-xl border border-slate-200 text-slate-900 hover:bg-slate-50 active:scale-[.98]"
              onClick={() => onClose?.()}
              title="Cerrar"
            >
              ✕
            </button>
          </div>
        </div>

        {/* filtros */}
        <div className="px-5 py-4 border-b bg-white">
          {!canQuery ? (
            <div className="text-sm text-red-600 font-semibold">
              Este contacto no tiene número de contrato. No se puede consultar historial.
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 md:col-span-4">
                <label className="text-xs font-extrabold text-slate-700">Buscar en novedad</label>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Ej: pago, visita, reclamo…"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                />
              </div>

              <div className="col-span-6 md:col-span-2">
                <label className="text-xs font-extrabold text-slate-700">Desde</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                />
              </div>

              <div className="col-span-6 md:col-span-2">
                <label className="text-xs font-extrabold text-slate-700">Hasta</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                />
              </div>

              <div className="col-span-6 md:col-span-2">
                <label className="text-xs font-extrabold text-slate-700">Contestar</label>
                <select
                  value={contestar}
                  onChange={(e) => setContestar(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                >
                  <option value="">Todos</option>
                  <option value="1">Sí</option>
                  <option value="0">No</option>
                </select>
              </div>

              <div className="col-span-6 md:col-span-2">
                <label className="text-xs font-extrabold text-slate-700">Orden</label>
                <select
                  value={order}
                  onChange={(e) => setOrder(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                >
                  <option value="desc">Más reciente</option>
                  <option value="asc">Más antiguo</option>
                </select>
              </div>

              <div className="col-span-12 md:col-span-2">
                <label className="text-xs font-extrabold text-slate-700">Tamaño</label>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* body */}
        <div className="p-5 bg-slate-50">
          {err ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700 font-semibold">
              {err}
            </div>
          ) : null}

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            {/* header tabla */}
            <div className="grid grid-cols-12 gap-0 px-4 py-3 border-b bg-slate-50 text-xs font-extrabold text-slate-600">
              <div className="col-span-3 md:col-span-2">Fecha</div>
              <div className="col-span-2 md:col-span-1">Hora</div>
              <div className="col-span-2 md:col-span-2">Origen</div>
              <div className="col-span-2 md:col-span-2">Contestar</div>
              <div className="col-span-3 md:col-span-5">Novedad</div>
            </div>

            {/* rows */}
            <div className="max-h-[52vh] overflow-y-auto">
              {loading ? (
                <div className="p-6 text-slate-600">Cargando…</div>
              ) : rows.length === 0 ? (
                <div className="p-6 text-slate-500">No hay bitácoras con esos filtros.</div>
              ) : (
                rows.map((r, idx) => {
                  const contest = Number(r.contestar) ? "Sí" : "No";
                  return (
                    <div
                      key={`${idx}_${r.fecha}_${r.hora}`}
                      className="grid grid-cols-12 gap-0 px-4 py-3 border-b last:border-b-0 hover:bg-slate-50"
                    >
                      <div className="col-span-3 md:col-span-2 text-sm text-slate-900 font-semibold">
                        {fmtDate(r.fecha)}
                      </div>
                      <div className="col-span-2 md:col-span-1 text-sm text-slate-700">
                        {fmtTime(r.hora)}
                      </div>
                      <div className="col-span-2 md:col-span-2">
                        <span className="inline-flex items-center rounded-full border border-slate-200 px-2 py-0.5 text-xs font-bold text-slate-700 bg-white">
                          {r.origen || "—"}
                        </span>
                      </div>
                      <div className="col-span-2 md:col-span-2">
                        <span
                          className={cls(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-extrabold border",
                            contest === "Sí"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-slate-50 text-slate-700 border-slate-200"
                          )}
                        >
                          {contest}
                        </span>
                      </div>
                      <div className="col-span-3 md:col-span-5 text-sm text-slate-800 whitespace-pre-wrap">
                        {r.novedad || ""}
                        {r.usuario ? (
                          <div className="text-xs text-slate-500 mt-1">
                            Por: <b className="text-slate-700">{r.usuario}</b>
                            {typeof r.urgencia !== "undefined" ? (
                              <>
                                {" "}· Urgencia:{" "}
                                <b className="text-slate-700">{Number(r.urgencia) ? "Sí" : "No"}</b>
                              </>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* paginación */}
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-slate-600">
              Página <b className="text-slate-900">{page}</b> de{" "}
              <b className="text-slate-900">{pages}</b>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-900 font-bold disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={loading || page <= 1}
              >
                ←
              </button>

              {pageButtons.map((b, i) =>
                b === "…" ? (
                  <span key={`dots_${i}`} className="px-2 text-slate-500">…</span>
                ) : (
                  <button
                    key={`p_${b}`}
                    className={cls(
                      "px-3 py-1.5 rounded-xl border font-extrabold",
                      b === page
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-900 border-slate-200 hover:bg-slate-50"
                    )}
                    onClick={() => setPage(Number(b))}
                    disabled={loading}
                  >
                    {b}
                  </button>
                )
              )}

              <button
                className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-900 font-bold disabled:opacity-50"
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={loading || page >= pages}
              >
                →
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { transform: translateY(6px) scale(.985); opacity: .0; }
          to   { transform: translateY(0) scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}